import { Component, ChangeDetectionStrategy, signal, inject, computed, effect, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { LoginComponent } from './components/login/login.component';
import { RegisterComponent } from './components/register/register.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { AdminDashboardComponent } from './components/admin-dashboard/admin-dashboard.component';
import { PlansPageComponent } from './components/plans-page/plans-page.component';
import { ProfilePageComponent } from './components/profile-page/profile-page.component';
import { DRAFT_STORAGE_KEY } from './components/essay-editor/essay-editor.component';
import { AuthService } from './services/auth.service';
import { LogService } from './services/log.service';
import { PaymentService } from './services/payment.service';
import { CouponService } from './services/coupon.service';

type View = 'landing' | 'login' | 'register' | 'app' | 'admin' | 'plans' | 'profile';

@Component({
  selector: 'app-root',
  standalone: true,
  template: `
<div class="min-h-screen flex flex-col bg-surface-50">
  <div class="fixed bottom-5 right-5 z-50 w-full max-w-sm space-y-3">
    @for (toast of notifications(); track toast.id) {
      <div class="animate-toast-in flex items-start w-full max-w-sm p-4 bg-white/95 backdrop-blur-lg rounded-2xl shadow-elevated ring-1 ring-slate-900/5" role="alert">
        <div class="inline-flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-xl"
            [class.text-emerald-600]="toast.type === 'success'" [class.bg-emerald-50]="toast.type === 'success'"
            [class.text-red-600]="toast.type === 'error'" [class.bg-red-50]="toast.type === 'error'"
            [class.text-blue-600]="toast.type === 'info'" [class.bg-blue-50]="toast.type === 'info'">
          @if (toast.type === 'success') {
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" /></svg>
          } @else if (toast.type === 'error') {
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z M8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clip-rule="evenodd" /></svg>
          } @else {
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a1 1 0 0 0 0 2v3a1 1 0 0 0 1 1h1a1 1 0 1 0 0-2v-3a1 1 0 0 0-1-1H9z" clip-rule="evenodd"></path></svg>
          }
          <span class="sr-only">Icon</span>
        </div>
        <div class="ms-3 flex-1 min-w-0">
          <div class="text-sm font-semibold text-slate-900">{{ toast.title }}</div>
          <div class="text-sm text-slate-500 mt-0.5 leading-snug">{{ toast.message }}</div>
        </div>
        <button type="button" (click)="removeNotification(toast.id)" class="ms-3 -mt-0.5 flex-shrink-0 rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition" [attr.aria-label]="'Fechar'">
          <span class="sr-only">Fechar</span>
          <svg class="w-3.5 h-3.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/></svg>
        </button>
      </div>
    }
  </div>

  @if(currentView() !== 'admin') {
    <app-header
      [isAuthenticated]="isAuthenticated()"
      [isAdmin]="isAdmin()"
      [credits]="credits()"
      [plan]="userPlan()"
      [isPlansActive]="currentView() === 'plans'"
      [isProfileActive]="currentView() === 'profile'"
      (requestLogin)="onRequestLogin()"
      (requestRegister)="onRequestRegister()"
      (logout)="onLogout()"
      (viewPlans)="onViewPlans()"
      (viewProfile)="onViewProfile()"
      (requestLanding)="onRequestLanding()">
    </app-header>
  }

  <main class="flex-grow">
    @switch (currentView()) {
      @case ('landing') {
        <app-landing-page (requestRegister)="onRequestRegister()" (requestLogin)="onRequestLogin()"></app-landing-page>
      }
      @case ('login') {
        <app-login (loginSuccess)="onAuthSuccess()" (requestRegister)="onRequestRegister()"></app-login>
      }
      @case ('register') {
        <app-register (registerSuccess)="onAuthSuccess()" (requestLogin)="onRequestLogin()"></app-register>
      }
      @case ('app') {
        <app-dashboard (viewPlansRequest)="onViewPlans()"></app-dashboard>
      }
      @case ('plans') {
        <app-plans-page (requestBackToApp)="onBackToApp()" (requestLogin)="onRequestLogin()"></app-plans-page>
      }
      @case ('profile') {
        <app-profile-page (accountDeleted)="onAccountDeleted()" (backToApp)="onBackToApp()"></app-profile-page>
      }
      @case ('admin') {
        <app-admin-dashboard></app-admin-dashboard>
      }
    }
  </main>

  @if(currentView() !== 'admin') {
    <app-footer></app-footer>
  }
</div>
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LandingPageComponent,
    LoginComponent,
    RegisterComponent,
    DashboardComponent,
    PlansPageComponent,
    ProfilePageComponent,
    AdminDashboardComponent,
    HeaderComponent,
    FooterComponent,
  ],
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private logService = inject(LogService);
  private paymentService = inject(PaymentService);
  private couponService = inject(CouponService);
  private platformId = inject(PLATFORM_ID);

  readonly currentView = signal<View>('landing');
  readonly currentUser = this.authService.currentUser;
  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly isAdmin = this.authService.isAdmin;
  readonly hasActiveUser = computed(() => !!this.currentUser());
  readonly credits = this.authService.effectiveCredits;
  readonly userPlan = computed(() => this.currentUser()?.plan);
  readonly notifications = this.authService.notifications;

  constructor() {
    effect(() => {
      const authenticated = this.isAuthenticated();

      if (authenticated) {
        const pendingPriceId = this.authService.pendingPriceId();
        if (pendingPriceId) {
          this.handlePendingCheckout(pendingPriceId);
        } else {
          const current = this.currentView();
          const user = this.currentUser();

          // Aguarda o perfil para decidir a rota correta (evita cair em "app" antes de descobrir admin).
          if (!user) {
            if (current === 'app' || current === 'admin' || current === 'plans' || current === 'profile') {
              this.currentView.set('login');
            }
            return;
          }

          if (user.isAdmin) {
            if (current !== 'admin') {
              this.currentView.set('admin');
            }
            return;
          }

          if (current === 'landing' || current === 'login' || current === 'register' || current === 'admin') {
            this.currentView.set('app');
          }
        }
      } else if (!authenticated) {
        const current = this.currentView();
        if (current === 'app' || current === 'admin' || current === 'plans' || current === 'profile') {
          this.currentView.set('login');
        }
      }
    }, { allowSignalWrites: true });
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.suppressKnownConsoleNoiseForUsers();
      this.couponService.captureCouponFromUrl();
      try {
        await this.authService.finalizeOAuthRedirectIfNeeded();
      } catch (error: any) {
        const message = error?.message ?? 'Não foi possível concluir login com Google.';
        this.authService.showNotification('error', 'Erro no login com Google', message);
      }
      this.handlePaymentRedirect();
    }
  }

  private async handlePendingCheckout(priceId: string) {
    this.authService.setPendingPriceId(null);
    const pendingCouponCode = this.couponService.activeCouponCode();
    try {
      await this.paymentService.startCheckout(priceId, { preopen: false, couponCode: pendingCouponCode });
    } catch (error) {
      await this.logService.logError('App pending checkout failed', error, {
        priceId,
        couponCode: pendingCouponCode,
        isAuthenticated: this.isAuthenticated(),
        isAdmin: this.isAdmin(),
      });
      this.authService.showNotification('error', 'Falha no pagamento', 'Não foi possível iniciar o checkout pendente.');
      this.currentView.set(this.isAdmin() ? 'admin' : 'app');
    }
  }

  private suppressKnownConsoleNoiseForUsers() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalhost) {
      return;
    }

    const originalConsoleError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      const firstArg = typeof args[0] === 'string' ? args[0] : '';
      if (firstArg.startsWith('Failed to submit report:')) {
        return;
      }
      originalConsoleError(...args);
    };
  }

  private handlePaymentRedirect() {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout_status');

    if (checkoutStatus === 'success') {
      this.authService.showNotification('success', 'Pagamento recebido', 'Seus créditos serão atualizados em breve.');
      void this.refreshProfileAfterCheckout();
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (checkoutStatus === 'pending') {
      this.authService.showNotification('info', 'Pagamento pendente', 'Aguardando confirmação do Mercado Pago.');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (checkoutStatus === 'error') {
      this.authService.showNotification('error', 'Pagamento não concluído', 'O pagamento foi cancelado, recusado ou não pôde ser concluído.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  private async refreshProfileAfterCheckout(): Promise<void> {
    const delaysMs = [0, 1500, 4000, 8000];

    for (const delay of delaysMs) {
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      await this.authService.refreshUserProfile().catch(() => undefined);
    }
  }

  removeNotification(id: number) {
    this.authService.removeNotification(id);
  }

  onRequestLogin() {
    this.currentView.set('login');
  }

  onRequestRegister() {
    this.currentView.set('register');
  }

  onRequestLanding() {
    if (this.isAuthenticated()) {
      this.currentView.set(this.isAdmin() ? 'admin' : 'app');
      return;
    }
    this.currentView.set('landing');
  }

  onAuthSuccess() {
    if (!this.isAuthenticated()) return;

    const user = this.currentUser();
    if (!user) return;

    this.currentView.set(user.isAdmin ? 'admin' : 'app');
  }

  onLogout() {
    this.currentView.set('login');
    this.authService.setPendingPriceId(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
    this.authService.showNotification('info', 'Até logo!', 'Você saiu da sua conta com segurança.');
    void this.authService.signOut();
  }

  onViewPlans() {
    if (this.isAuthenticated()) {
      this.currentView.set('plans');
      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    this.currentView.set('landing');
    if (isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const plansElement = document.getElementById('plans');
          if (plansElement) {
            plansElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 0);
      });
    }
  }

  onViewProfile() {
    if (!this.isAuthenticated()) {
      this.currentView.set('login');
      return;
    }

    this.currentView.set('profile');
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onBackToApp() {
    if (!this.isAuthenticated()) {
      this.currentView.set('login');
      return;
    }
    this.currentView.set(this.isAdmin() ? 'admin' : 'app');
  }

  onAccountDeleted() {
    this.authService.setPendingPriceId(null);
    this.currentView.set('login');
  }
}
